// src/lib/myVersion.js — T24: "my version" is a POSTING, so it needs values to render.
//
// OWNER 2026-09-03: "we need my version to be listing not list in listing." My version stopped
// being a list of fields sitting inside the property card and became a row in POSTED ON that
// OPENS — the same card, showing the ad with the agent's own recorded values standing in.
//
// 🔴 IT IS AN OVERLAY, NEVER A SUBSTITUTE, and that is the whole design. An agent records a
// handful of facts (measured 2026-09-03: 92 own-assertion claims across the whole corpus — 30
// `agent`, 62 `owner`). Rendering "my version" as only those facts would draw a listing with no
// price, no address and no photos and call it the agent's version of the property, which is a
// lie about what they believe. Their version is *the ad, corrected where they corrected it*.
//
// ⚠️ Pure and separate from the modal because the modal cannot be rendered in a harness (leaflet
// wants a DOM) and cannot be opened as an agent in verification (only an agent's own session
// carries their `own` facts). This is the part that CAN be proven, so it is the part that is.

// Which facts count as the agent's own. The server decides this — `state === 'own'` comes from
// 0439, which deliberately excludes `ai`: a machine reading of an ad is an interpretation, not
// the agent's claim. Never widen it here; the filter has one home.
export function ownValuesOf(facts) {
  return Object.fromEntries(Object.entries(facts || {})
    .filter(([, f]) => f && f.state === 'own')
    .map(([k, f]) => [k, f.value]))
}

/**
 * Every fact the agent recorded, in a stable order, for a row that has to NAME them.
 *
 * ⚠️ WHY THIS EXISTS. The my-version row rendered `FactsDiffLine`, which is the CARD-FACE
 * summary: area, rooms, bedrooms, floor, bathrooms, condition, and nothing else. So an agent who
 * recorded a ceiling height, a kitchen area, a balcony count, a year built or a power capacity
 * saw a row reading "my version — facts you recorded" with NOT ONE of them named. Found on
 * production 2026-09-04 by recording a ceiling height and looking for it: it was stored, it was
 * attributed, and it appeared nowhere on the card. That is a label with nothing behind it —
 * diagnosis rule 6 — on the one surface whose entire job is to show the agent their own work.
 *
 * A recorded NULL is kept and marked, never dropped: clearing a wrong floor number is an
 * assertion ("this is not known"), and a summary that omits it would report the agent's
 * correction as if they had never made it.
 */
export function ownFactSummary(facts) {
  return Object.entries(facts || {})
    .filter(([, f]) => f && f.state === 'own')
    .map(([key, f]) => ({ key, value: f.value, cleared: f.value === null || f.value === undefined || f.value === '' }))
    .sort((a, b) => a.key.localeCompare(b.key))
}

// True when there is a my-version posting to open at all. An empty one would be a door onto the
// ad you are already looking at.
//
// ⚠️ PHOTOGRAPHS COUNT AS A VERSION ON THEIR OWN (owner 2026-09-03: "also images whatever agent
// edits in gallery is their version"). Gated on facts alone, an agent who replaced the pictures
// and corrected nothing else would have NO my-version row — their version would exist and be
// unreachable, which is the exact defect this step fixed for facts.
//
// 🔴 KNOWN LIMITATION, recorded rather than guessed around: image sets carry no per-agent
// attribution (`manual`/`sentavus` means "ours"), so a colleague's upload makes this true for
// every agent on the flat. Facts are per-agent and exact; photographs are agency-level. Until
// per-agent image attribution exists this is the honest approximation — and the alternative,
// dropping image-only versions, makes the owner's ruling inert.
export function hasMyVersion(facts, sets) {
  if (Object.values(facts || {}).some(f => f && f.state === 'own')) return true
  return !!myVersionImageSet(sets)
}

/**
 * The ad, with the agent's recorded facts standing in.
 *
 * ⚠️ A recorded NULL is a recorded fact and must win. An agent who cleared a wrong floor number
 * is asserting "this is not known", and falling back to the ad's value there would silently
 * restore exactly what they removed. So the test is key PRESENCE, never truthiness — which is
 * why this cannot be written as `{ ...merged, ...own }` with the nulls stripped, the shape the
 * first draft had.
 */
export function myVersionOf(merged, facts) {
  const own = ownValuesOf(facts)
  if (!Object.keys(own).length) return merged
  return { ...(merged || {}), ...own }
}

// ── Photographs ────────────────────────────────────────────────────────────────────────────────
//
// OWNER 2026-09-03: "also images whatever agent edits in gallery is their version."
//
// A property carries several image SETS — one per portal posting, plus whatever an agent uploaded
// or edited in the gallery. Which one the hero shows is normally the agent's tab choice. When MY
// VERSION is open it is not a choice: my version shows MY photographs, or it is not my version.
//
// 🔑 `manual` / `sentavus` is the ONE definition of "ours" and it is not invented here — it is
// `lib/image_selection.js`'s top rung ("a deliberate act by a person"), the same rule the client
// share link and the card strip already use. If that list ever grows, it grows THERE.
export const OUR_IMAGE_SOURCES = new Set(['manual', 'sentavus'])

/**
 * The set MY VERSION should show, or null to leave the current choice alone.
 *
 * ⚠️ FALLS BACK TO THE AD, deliberately. An agent who has uploaded nothing has not asserted
 * anything about the photographs, and showing them an empty gallery under "my version" would
 * state an absence they never claimed — the same overlay rule as the facts above.
 *
 * ⚠️ It is "OURS", not "MINE", and the difference is real: image sets carry no per-agent
 * attribution, so a colleague's upload on the same flat is indistinguishable from this agent's.
 * That is a data-layer gap, not something to paper over with a guess here — if per-agent image
 * attribution ever lands, this is the one place that has to learn about it.
 */
export function myVersionImageSet(sets) {
  const all = Array.isArray(sets) ? sets : []
  // ⚠️ Another agent's VERSION is never "my version" (owner 2026-09-28: one version per agent per flat;
  // with 10+ agents on a flat the first version found was usually a colleague's). The server marks
  // version sets (`is_version`) and the viewer's own (`mine`); older servers send neither, which
  // leaves the previous agency-level behaviour unchanged.
  const ours = all.filter(s => OUR_IMAGE_SOURCES.has(s?.source) && (s?.urls || []).length && !(s.is_version && !s.mine))
  if (!ours.length) return null
  // AN AGENT'S VERSION = ITS CAPTURE'S PHOTOS + THE AGENT'S OWN (owner 2026-09-28: "ss stays as it was
  // but my version is ss photos plus what I added"). A set from a version listing (the server stamps
  // `based_on_listing_id` on those) is shown as the capture's first set followed by the agent's uploads,
  // deduplicated — the same composition the card strip gets (server.js fetchThumbnails).
  // ⚠️ The capture's FIRST set is the server's own pick: its image-sets order puts the clean rendition
  // of each listing before the watermarked one and the thumbnails, so this never shows the logo copy.
  const version = ours.find(s => s.mine && s.based_on_listing_id) || ours.find(s => s.based_on_listing_id)
  if (version) {
    const base = all.find(s => s.listing_id === version.based_on_listing_id && !OUR_IMAGE_SOURCES.has(s?.source) && (s?.urls || []).length)
    if (base) {
      const urls = [...new Set([...base.urls, ...version.urls])]
      return { ...version, urls, count: urls.length }
    }
    return version
  }
  // Newest first when the server gives us a date: an agent who re-uploaded meant the new set.
  // Without dates, the server's own order stands rather than a guess.
  return ours.slice().sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))[0]
}

/**
 * Every VERSION set shown as the version actually is: its capture's photos, then the agent's own
 * (owner 2026-09-28: "my version is ss photos plus what I added"). The server keeps them as separate
 * evidence sets; this composes them for display, once, for every surface that renders sets (tabs,
 * hero, my-version). The viewer's own version is moved to the front, so it is what opens by default.
 * ⚠️ Composition is idempotent (urls are deduplicated), so myVersionImageSet can run on the result.
 * ⚠️ The capture set is its FIRST non-ours set in server order — the clean rendition, never the logo copy.
 */
export function composeVersionSets(sets) {
  const all = Array.isArray(sets) ? sets : []
  const out = all.map(s => {
    if (!s?.is_version || !s.based_on_listing_id) return s
    const base = all.find(b => b.listing_id === s.based_on_listing_id && !OUR_IMAGE_SOURCES.has(b?.source) && (b?.urls || []).length)
    if (!base) return s
    const own = s.own_urls || s.urls || []   // the agent's own photos, kept apart so re-composing is exact
    const urls = [...new Set([...base.urls, ...own])]
    return { ...s, urls, count: urls.length, own_urls: own, own_count: own.length }
  })
  const mineIdx = out.findIndex(s => s?.is_version && s.mine)
  return mineIdx > 0 ? [out[mineIdx], ...out.slice(0, mineIdx), ...out.slice(mineIdx + 1)] : out
}
